import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CourseTesterAccess, portalApi } from '../services/portal';

/** This rollout is a staging surface, not an automatic signup entitlement. */
export function isCourseTesterAccessSurface(): boolean {
  return process.env.REACT_APP_HAURAKI_ENVIRONMENT === 'staging'
    || window.location.hostname === 'staging.hauraki.ai';
}

export function useCourseTesterAccess(userId?: string, refreshKey = 0) {
  const enabled = isCourseTesterAccessSurface() && !!userId;
  const [state, setState] = useState<{
    userId?: string;
    data: CourseTesterAccess | null;
    error: boolean;
    loading: boolean;
  }>({ data: null, error: false, loading: enabled });
  const [clockRevision, tick] = useState(0);
  const requestGeneration = useRef(0);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled || inFlight.current) return;
    const generation = ++requestGeneration.current;
    inFlight.current = true;
    setState((previous) => ({
      userId,
      data: previous.userId === userId ? previous.data : null,
      error: false,
      loading: true,
    }));
    try {
      const data = await portalApi.getCourseTesterAccess();
      if (generation === requestGeneration.current) setState({ userId, data, error: false, loading: false });
    } catch {
      if (generation === requestGeneration.current) setState({ userId, data: null, error: true, loading: false });
    } finally {
      if (generation === requestGeneration.current) inFlight.current = false;
    }
  }, [enabled, userId]);

  useEffect(() => {
    void refresh();
    return () => { requestGeneration.current += 1; inFlight.current = false; };
  }, [refresh, refreshKey]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onFocus = () => { void refresh(); };
    const poll = window.setInterval(() => {
      // Avoid background polling when a tester leaves this browser tab.
      if (document.visibilityState !== 'hidden') void refresh();
    }, 30_000);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(poll);
    };
  }, [enabled, refresh]);

  const data = enabled && state.userId === userId ? state.data : null;
  const courses = useMemo(() => {
    // The timer revision forces a fresh wall-clock expiry check without
    // recreating the dependency array on unrelated portal renders.
    void clockRevision;
    return data?.environment === 'staging'
      ? data.courses.filter((course) => Date.parse(course.expiresAt) > Date.now())
      : [];
  }, [data, clockRevision]);
  useEffect(() => {
    if (courses.length === 0) return undefined;
    const delay = Math.min(...courses.map((course) => Date.parse(course.expiresAt) - Date.now()));
    const timer = window.setTimeout(() => tick((value) => value + 1), Math.min(Math.max(delay, 1), 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [courses]);

  return {
    courses,
    hasCourseTesterGrant: data?.environment === 'staging' && data.hasCourseTesterGrant === true,
    // Revalidation must not tear down the home/course UI every 30 seconds.
    isLoading: enabled && (state.userId !== userId || (state.loading && state.data === null)),
    error: enabled && state.userId === userId && state.error,
    refresh,
  };
}
